'use client';

import { ArrowRight } from 'lucide-react';

import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import { Button } from '@/components/ui/button';
import { Price } from '@/components/ui/riyal';
import { WatchVideoPill } from '@/components/watch-video-pill';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { PRICING_FAQS } from '@/lib/pricing-faqs';
import { cn } from '@/lib/utils';

/* -------------------------------------------------------------------------- */
/*  Locale-keyed content                                                      */
/* -------------------------------------------------------------------------- */

const CONTENT = {
  en: {
    heroEyebrow: 'Pricing',
    heroTitle: 'Pricing built for support teams of every size.',
    heroSubtitle:
      'Pay only for outcomes Open delivers. No setup fees, no per-seat fees, no per-message fees.',
    priceUnit: '/ per resolution',
    priceBig: '75¢–90¢',
    priceIsRiyal: false,
    priceBlurb:
      'Your rate sits in this range based on monthly volume. Access the full Open platform with simple pricing — no setup fees, monthly fees, or hidden fees.',
    getStarted: 'Get started',
    videoLabel: 'Understand Open pricing in a minute',
    videoTitle: 'Open Pricing — 75¢–90¢ Per Resolved Ticket, No Per-Seat Fees',
    priceFooter:
      'Higher volume, lower rate. Final price is locked in writing before you go live.',
    guaranteeAria: 'The $2 million refund guarantee — learn more',
    guaranteeEyebrow: 'Refund Guarantee',
    guaranteeLine: 'Backed by our $2 million refund guarantee.',
    learnMore: 'Learn more',
    navStandard: 'Standard pricing',
    navFaqs: 'FAQs',
    contactSales: 'Contact sales',
    standardEyebrow: 'Standard pricing',
    standardTitle: 'Standard pricing for every feature',
    standardSubtitle:
      'One simple rate for outcomes Open delivers. The full platform — every channel, the helpdesk, telephony, outbound, workflows, the app builder — is available.',
    productTitle: 'AI Resolutions',
    productTagline:
      'The complete AI agent — one rate per fully resolved ticket, scaled to your volume.',
    productCta: 'Learn more',
    productBullets: [
      'Resolves end-to-end across every channel — voice, chat, email, WhatsApp, social.',
      'Free if Open hands the ticket to a human agent.',
      'Your rate lands between 75¢ and 90¢ based on monthly volume.',
      'No per-seat, per-message, or per-channel fees, ever.',
    ],
    productItems: [
      {
        heading: 'Per resolved ticket',
        description: 'Volume-based rate, locked in writing before you go live.',
        price: '$0.75 – $0.90',
        detail: 'per resolved ticket',
      },
      {
        heading: 'Human-handled tickets',
        description:
          'If Open hands a ticket to one of your agents, you’re not billed for it.',
        price: 'Free',
      },
      {
        heading: 'Volume discount',
        description:
          'Higher monthly volume automatically lowers your per-resolution rate.',
        price: 'Automatic',
      },
      {
        heading: 'Enterprise volume',
        description:
          'Above 100k resolutions/month we quote a custom rate. Email pricing@open.cx.',
        price: 'Custom',
      },
    ],
    replacesLabel: 'Replaces',
    includedTitle: 'Everything else, available.',
    includedSubtitle:
      'The full platform is available to you. Channels, the helpdesk, telephony, outbound, workflows, the app builder, support.',
    includedFeatures: [
      {
        title: 'AI-first channels',
        tagline:
          'Voice, chat, email, WhatsApp & SMS, Slack, Teams, social — one agent, every surface.',
        price: 'Available',
        detail: 'Carrier minutes at-cost (<$0.10 / resolution)',
      },
      {
        title: 'AI-native helpdesk',
        tagline:
          'AI-native ticketing — inbox, macros, SLAs, routing, reporting. Or bring your own.',
        price: 'Available',
        detail: 'Unlimited agent seats',
        replaces:
          'Zendesk · Intercom · Freshdesk · Salesforce · HubSpot · Front',
      },
      {
        title: 'AI Voice',
        tagline:
          'Cloud telephony stack — local numbers in 100+ countries, recording, transcription, SIP trunking.',
        price: 'Available',
        detail: 'Carrier minutes at-cost',
        replaces:
          'Aircall · Dialpad · RingCentral · Twilio Flex · Five9 · Genesys',
      },
      {
        title: 'AI Outbound engine',
        tagline:
          'AI-personalized multi-channel sequences with bulk imports, response branching, and full API.',
        price: 'Available',
        replaces:
          'Intercom Series · HubSpot Sequences · Salesforce Marketing Cloud · Mailchimp · Brevo',
      },
      {
        title: 'AI Agentic workflows',
        tagline:
          'Visual workflow builder, agent actions on every API, and 50+ pre-built playbooks.',
        price: 'Available',
        replaces: 'Zapier · n8n · Workato · Tray.io · Make',
      },
      {
        title: 'App Builder',
        tagline:
          "Ask Companion for a feature that doesn't exist yet and it builds it into the platform, live in the channel your team already works in.",
        price: 'Available',
      },
      {
        title: 'Knowledge management',
        tagline:
          'Auto-ingest docs and articles, surface gaps automatically, and learn from every resolved ticket. Customer Insights and VoC built in.',
        price: 'Available',
        replaces:
          'Guru · Notion · Confluence · Helpjuice · Stonly · Stella Connect',
      },
      {
        title: 'Implementation & support',
        tagline:
          'Named solutions engineer + AI trainer, 24/7 support, white-glove onboarding, $2M refund guarantee.',
        price: 'Available',
      },
    ],
    faqsEyebrow: 'FAQs',
    faqsTitle: 'Pricing, in plain English.',
    faqsSubtitle:
      "Common questions about pricing, billing, and how the guarantee fits in. Email pricing@open.cx for anything that isn't here.",
    faqs: PRICING_FAQS.en,
  },
  ar: {
    heroEyebrow: 'الأسعار',
    heroTitle: 'تسعير مصمّم لفرق الدعم، مهما كان حجمها.',
    heroSubtitle:
      'ادفع بس مقابل النتائج اللي يحققها Open. بدون رسوم إعداد، ولا رسوم على كل موظف، ولا رسوم على كل رسالة.',
    priceUnit: '/ لكل حل',
    // The sign, not the word. U+20C1 renders via the Saudi Riyal face in
    // globals.css; <Price> supplies the accessible name, since a screen
    // reader predating Unicode 17 announces nothing for the glyph itself.
    priceBig: '٢-٣',
    priceIsRiyal: true,
    priceBlurb:
      'سعرك يقع ضمن هذا المدى حسب حجمك الشهري. والمنصة كاملة بين يديك بتسعير بسيط — بدون رسوم إعداد، ولا رسوم شهرية، ولا رسوم مخفية.',
    getStarted: 'ابدأ الآن',
    videoLabel: 'افهم أسعار Open بدقيقة',
    videoTitle: 'أسعار Open — ٢-٣ ⃁ لكل تذكرة تُحلّ، بدون رسوم على كل موظف',
    priceFooter: 'حجم أكبر، سعر أقل. نثبّت السعر النهائي كتابياً قبل ما تنطلق.',
    guaranteeAria: 'ضمان استرجاع حتى ٧,٥ مليون ⃁ — اعرف أكثر',
    guaranteeEyebrow: 'ضمان الاسترجاع',
    guaranteeLine: 'مدعوم بضمان استرجاع حتى ٧,٥ مليون ⃁.',
    learnMore: 'اعرف أكثر',
    navStandard: 'الأسعار القياسية',
    navFaqs: 'الأسئلة الشائعة',
    contactSales: 'تواصل مع المبيعات',
    standardEyebrow: 'الأسعار القياسية',
    standardTitle: 'سعر قياسي واحد لكل ميزة',
    standardSubtitle:
      'سعر واحد بسيط مقابل النتائج اللي يحققها Open. والمنصة كاملة متاحة — كل قناة، ونظام الدعم، والاتصالات، والتسلسلات الصادرة، وسير العمل، وبنّاء التطبيقات.',
    productTitle: 'حلّ التذاكر بالـ AI',
    productTagline:
      'الوكيل الذكي بكامل قدراته — سعر واحد لكل تذكرة تُحلّ بالكامل، يتعدّل حسب حجمك الشهري.',
    productCta: 'اعرف أكثر',
    productBullets: [
      'يحل من البداية إلى النهاية عبر كل قناة — صوت، محادثة، إيميل، WhatsApp، ومنصات التواصل.',
      'مجاناً إذا سلّم Open التذكرة لوكيل بشري.',
      'سعرك يقع بين ٢ و٣ ⃁، يتحدّد حسب حجمك الشهري.',
      'بدون رسوم على كل موظف، ولا على كل رسالة، ولا على كل قناة — أبداً.',
    ],
    productItems: [
      {
        heading: 'لكل تذكرة تُحلّ',
        description: 'سعر حسب الحجم، نثبّته كتابياً قبل ما تنطلق.',
        price: '٢ - ٣ ⃁',
        detail: 'لكل تذكرة تُحلّ',
      },
      {
        heading: 'التذاكر اللي يتولّاها موظف بشري',
        description: 'إذا سلّم Open تذكرة لأحد وكلائك، فما تُحتسب عليك.',
        price: 'مجاناً',
      },
      {
        heading: 'خصم الحجم',
        description: 'كلما زاد حجمك الشهري، انخفض سعر الحل تلقائياً.',
        price: 'تلقائي',
      },
      {
        heading: 'حجم المؤسسات',
        description:
          'فوق ١٠٠ ألف حل شهرياً، نضع لك سعراً مخصصاً. راسل pricing@open.cx.',
        price: 'مخصص',
      },
    ],
    replacesLabel: 'يحل محل',
    includedTitle: 'وكل الباقي، متاح.',
    includedSubtitle:
      'المنصة كاملة متاحة لك. القنوات، ونظام الدعم، والاتصالات، والتسلسلات الصادرة، وسير العمل، وبنّاء التطبيقات، والدعم.',
    includedFeatures: [
      {
        title: 'قنوات مبنية على AI',
        tagline:
          'صوت، محادثة، إيميل، WhatsApp وSMS، Slack، Teams، وسوشيال — وكيل واحد، وين ما كان العميل.',
        price: 'متاح',
        detail: 'دقائق المشغّل بسعر التكلفة (<٠,٤٠ ⃁ لكل حل)',
      },
      {
        title: 'نظام دعم مبني على AI',
        tagline:
          'نظام تذاكر ذكي من أساسه — صندوق وارد، وردود جاهزة، وSLAs، وتوجيه، وتقارير. أو استخدم نظامك الحالي.',
        price: 'متاح',
        detail: 'عدد موظفين بلا حدود',
        replaces:
          'Zendesk · Intercom · Freshdesk · Salesforce · HubSpot · Front',
      },
      {
        title: 'AI Voice',
        tagline:
          'منظومة اتصالات سحابية — أرقام محلية في +١٠٠ دولة، تسجيل، تفريغ نصي، SIP trunking.',
        price: 'متاح',
        detail: 'دقائق المشغّل بسعر التكلفة',
        replaces:
          'Aircall · Dialpad · RingCentral · Twilio Flex · Five9 · Genesys',
      },
      {
        title: 'محرّك التسلسلات الصادرة بالـ AI',
        tagline:
          'تسلسلات متعددة القنوات مخصّصة بالـ AI، مع استيراد بالجملة، وتفريع الردود، وواجهة API كاملة.',
        price: 'متاح',
        replaces:
          'Intercom Series · HubSpot Sequences · Salesforce Marketing Cloud · Mailchimp · Brevo',
      },
      {
        title: 'سير العمل الذكي',
        tagline:
          'محرّر مرئي لسير العمل، وإجراءات للوكيل على أي API، و+٥٠ دليل عمل جاهز.',
        price: 'متاح',
        replaces: 'Zapier · n8n · Workato · Tray.io · Make',
      },
      {
        title: 'بنّاء التطبيقات',
        tagline:
          'اطلب من Companion ميزة ما هي موجودة، ويبنيها داخل المنصة مباشرة، في القناة اللي يشتغل فيها فريقك.',
        price: 'متاح',
      },
      {
        title: 'إدارة المعرفة',
        tagline:
          'يستخرج المستندات والمقالات تلقائياً، ويكشف الثغرات من تلقاء نفسه، ويتعلّم من كل تذكرة تُحلّ. ورؤى العملاء وصوت العميل مدمجة في المنصة.',
        price: 'متاح',
        replaces:
          'Guru · Notion · Confluence · Helpjuice · Stonly · Stella Connect',
      },
      {
        title: 'التنفيذ والدعم',
        tagline:
          'مهندس حلول مخصص لك + مدرّب للذكاء الاصطناعي، دعم على مدار الساعة، إعداد بعناية فائقة، وضمان استرجاع حتى ٧,٥ مليون ⃁.',
        price: 'متاح',
      },
    ],
    faqsEyebrow: 'الأسئلة الشائعة',
    faqsTitle: 'الأسعار، بكل وضوح.',
    faqsSubtitle:
      'أسئلة شائعة عن الأسعار، والفوترة، ووين يقع الضمان من كل هذا. وإذا ما وجدت سؤالك هنا، راسلنا على pricing@open.cx.',
    faqs: PRICING_FAQS.ar,
  },
} as const;

type Content = (typeof CONTENT)['en'];

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

export default function PricingPage() {
  const { locale } = useTranslation();
  const isRtl = locale === 'ar';
  const c = isRtl ? (CONTENT.ar as unknown as Content) : CONTENT.en;

  return (
    <main className="bg-white">
      <Hero c={c} isRtl={isRtl} />
      <SectionNav c={c} />
      <StandardPricing c={c} />
      <Faqs c={c} />
      <CustomerVideos />
    </main>
  );
}

/* -------------------------------------------------------------------------- */
/*  Hero — two-up plan cards (Stripe-style)                                   */
/* -------------------------------------------------------------------------- */

const Hero = ({ c, isRtl }: { c: Content; isRtl: boolean }) => (
  <section className="bg-[#fafaf9] pt-32 pb-16 md:pt-40 md:pb-20">
    <div className="container">
      <div className="mb-12 max-w-3xl md:mb-16">
        <p className="mb-4 text-xs font-medium tracking-widest rtl:tracking-normal text-neutral-500 uppercase">
          {c.heroEyebrow}
        </p>
        <h1 className="font-serif text-balance text-neutral-900 text-[2rem] md:text-4xl xl:text-5xl">
          {c.heroTitle}
        </h1>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
          {c.heroSubtitle}
        </p>
      </div>

      {/* Single pricing card with the $2M guarantee as a clickable footer
          attached to the bottom. The guarantee is visually distinct
          (dark, video background) so nobody mistakes it for a plan. */}
      <article className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
        <div className="flex flex-col p-8 md:p-10">
          <div>
            <h2 className="flex items-baseline gap-2 font-serif text-neutral-900">
              <span className="text-5xl md:text-6xl">
                {c.priceIsRiyal ? <Price amount={c.priceBig} /> : c.priceBig}
              </span>
              <span className="text-base font-normal text-neutral-500">
                {c.priceUnit}
              </span>
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-500 md:text-base">
              {c.priceBlurb}
            </p>
          </div>

          <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
            <Button
              asChild
              className="h-11 rounded-full bg-neutral-900 px-5 text-sm font-medium text-white hover:bg-neutral-800"
            >
              <Link
                href="/demo"
                prefetch
                aria-label={
                  isRtl
                    ? 'اعرف أكثر عن Open واحجز عرضًا توضيحيًا'
                    : 'Learn more about Open and book a demo'
                }
              >
                {c.getStarted}
                <ArrowRight className="ltr:ml-1 rtl:mr-1 rtl:rotate-180 h-4 w-4" />
              </Link>
            </Button>

            <WatchVideoPill
              tone="light"
              reverse
              videoId="inv3VUYFm7E"
              label={c.videoLabel}
              duration="1:24"
              title={c.videoTitle}
            />
          </div>

          <div className="mt-10 border-t border-neutral-200 pt-6">
            <p className="text-sm text-neutral-500">{c.priceFooter}</p>
          </div>
        </div>

        {/* Guarantee footer — clickable, attached to the bottom of the
            pricing card so it reads as a trust seal rather than a plan. */}
        <Link
          href="/guarantee"
          prefetch
          aria-label={c.guaranteeAria}
          className="group relative isolate block overflow-hidden border-t border-neutral-200 bg-neutral-950 px-8 py-6 text-white md:px-10 md:py-7"
        >
          <video
            src="/home/features/challange.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover opacity-45 transition-opacity duration-300 group-hover:opacity-60"
          />
          {/* Scrim that darkens the side the headline sits on. The gradient
              angle is physical, so it has to be mirrored for RTL — otherwise
              the Arabic headline lands on the brightest end of the video. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 -z-10"
            style={{
              background: `linear-gradient(${isRtl ? 270 : 90}deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0.4) 100%)`,
            }}
          />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-medium tracking-[0.18em] rtl:tracking-normal text-amber-400 uppercase">
                {c.guaranteeEyebrow}
              </p>
              <p className="mt-1.5 font-sans text-base font-medium text-white md:text-lg">
                {c.guaranteeLine}
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-white transition-colors group-hover:text-amber-400">
              {c.learnMore}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5" />
            </span>
          </div>
        </Link>
      </article>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Sticky section nav (Standard / Custom / FAQs)                             */
/* -------------------------------------------------------------------------- */

const SectionNav = ({ c }: { c: Content }) => {
  const { t } = useTranslation();
  const items: { href: string; label: string }[] = [
    { href: '#standard', label: c.navStandard },
    { href: '#faqs', label: c.navFaqs },
  ];

  return (
    <div className="sticky top-[var(--header-height)] z-30 border-y border-neutral-200 bg-white/85 backdrop-blur">
      <div className="container">
        <nav className="flex items-center gap-1 overflow-x-auto py-3 md:gap-2">
          {items.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="rounded-full px-3.5 py-1.5 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
            >
              {item.label}
            </a>
          ))}
          <div className="ltr:ml-auto rtl:mr-auto hidden md:block">
            <Button
              asChild
              variant="outline"
              className="h-9 rounded-full border-neutral-300 bg-white px-4 text-sm font-medium text-neutral-900 hover:bg-neutral-50"
            >
              <Link
                href="/demo"
                prefetch
                aria-label={t.common['Learn more about Open and book a demo']}
              >
                {c.contactSales}
              </Link>
            </Button>
          </div>
        </nav>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Standard pricing                                                          */
/* -------------------------------------------------------------------------- */

const StandardPricing = ({ c }: { c: Content }) => (
  <section id="standard" className="scroll-mt-32 bg-[#fafaf9] py-20 md:py-28">
    <div className="container">
      {/* Section header */}
      <div className="mb-12 max-w-2xl md:mb-16">
        <p className="mb-3 text-xs font-medium tracking-widest rtl:tracking-normal text-neutral-500 uppercase">
          {c.standardEyebrow}
        </p>
        <h2 className="font-serif text-balance text-neutral-900 text-2xl md:text-4xl">
          {c.standardTitle}
        </h2>
        <p className="mt-4 text-base leading-relaxed text-neutral-500 md:text-lg">
          {c.standardSubtitle}
        </p>
      </div>

      {/* Pricing breakdown — Stripe-style line items */}
      <div>
        <div className="space-y-12 md:space-y-16">
          <PricingProduct
            title={c.productTitle}
            tagline={c.productTagline}
            ctaHref="/demo"
            ctaLabel={c.productCta}
            bullets={c.productBullets as unknown as string[]}
            items={c.productItems as unknown as PricingItem[]}
            replaces={{
              label: c.replacesLabel,
              logos: [
                { src: '/logos/logo-intercom.svg', alt: 'Fin by Intercom' },
                { src: '/logos/logo-zendesk.svg', alt: 'Zendesk AI agents' },
                {
                  src: '/logos/logo-salesforce.svg',
                  alt: 'Salesforce Einstein',
                },
                { src: '/logos/logo-hubspot.svg', alt: 'HubSpot Breeze AI' },
              ],
            }}
          />

          <IncludedFeatures c={c} />
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Included features (compact rows)                                          */
/* -------------------------------------------------------------------------- */

interface IncludedFeature {
  title: string;
  tagline: string;
  price: string;
  detail?: string;
  replaces?: string;
}

const INCLUDED_CTA_HREFS = [
  '/channels',
  '/helpdesks/open',
  '/channels/voice',
  '/outbound-sequencing',
  '/workflows',
  '/app-builder',
  '/knowledge-gap-detection',
  '/about-open',
];

const IncludedFeatures = ({ c }: { c: Content }) => (
  <article className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
    <div className="border-b border-neutral-200 px-8 py-7 md:px-10 md:py-8">
      <h3 className="font-serif text-xl text-neutral-900 md:text-2xl">
        {c.includedTitle}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-neutral-500 md:text-base">
        {c.includedSubtitle}
      </p>
    </div>

    <div className="divide-y divide-neutral-200">
      {(c.includedFeatures as unknown as IncludedFeature[]).map(
        (feature, i) => (
          <div
            key={feature.title}
            className="grid gap-4 px-8 py-6 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] sm:items-start sm:gap-8 md:px-10"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h4 className="font-sans text-base text-neutral-900">
                  {feature.title}
                </h4>
                <Link
                  href={INCLUDED_CTA_HREFS[i]}
                  prefetch
                  className="inline-flex items-center gap-0.5 text-xs font-medium text-neutral-500 transition-colors hover:text-neutral-900"
                >
                  {c.learnMore}
                  <ArrowRight className="ltr:ml-0.5 rtl:mr-0.5 rtl:rotate-180 h-3 w-3" />
                </Link>
              </div>
              <p className="mt-1.5 text-sm leading-relaxed text-neutral-500">
                {feature.tagline}
              </p>
              {feature.replaces && (
                <p className="mt-3 text-xs leading-relaxed text-neutral-500">
                  <span className="font-medium uppercase tracking-[0.14em] rtl:tracking-normal text-neutral-400">
                    {c.replacesLabel}
                  </span>{' '}
                  <span className="text-neutral-600">{feature.replaces}</span>
                </p>
              )}
            </div>

            <div className="sm:ltr:text-right sm:rtl:text-left">
              <p className="text-base font-medium text-neutral-900">
                {feature.price}
              </p>
              {feature.detail && (
                <p className="mt-1 text-xs leading-relaxed text-neutral-500">
                  {feature.detail}
                </p>
              )}
            </div>
          </div>
        ),
      )}
    </div>
  </article>
);

/* -------------------------------------------------------------------------- */
/*  Pricing product block (one section per "product")                         */
/* -------------------------------------------------------------------------- */

interface PricingItem {
  heading: string;
  description: string;
  price: string;
  detail?: string;
}

/** A price that leads with a currency symbol or a digit (Latin or Arabic-Indic). */
const NUMERIC_PRICE = /^[$\d٠-٩۰-۹\u20C1]/;

interface ReplacesLogo {
  src: string;
  alt: string;
  /** Optional CSS height override (defaults to h-5) */
  className?: string;
}

interface PricingProductProps {
  title: string;
  tagline: string;
  ctaHref: string;
  ctaLabel?: string;
  bullets: string[];
  items: PricingItem[];
  /** Optional "Replaces XYZ" strip rendered at the bottom of the left column. */
  replaces?: { label?: string; logos: ReplacesLogo[] };
}

const PricingProduct = ({
  title,
  tagline,
  ctaHref,
  ctaLabel = 'Learn more',
  bullets,
  items,
  replaces,
}: PricingProductProps) => (
  <article className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
    <div className="grid gap-y-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
      {/* Left column — title + bullets + CTA */}
      <div className="border-b border-neutral-200 p-8 md:p-10 lg:border-b-0 lg:ltr:border-r lg:rtl:border-l">
        <h3 className="font-serif text-xl text-neutral-900 md:text-2xl">
          {title}
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-neutral-500 md:text-base">
          {tagline}
        </p>
        <div className="mt-6">
          <Button
            asChild
            variant="outline"
            className="h-10 rounded-full border-neutral-300 bg-white px-4 text-sm font-medium text-neutral-900 hover:bg-neutral-50"
          >
            <Link href={ctaHref} prefetch>
              {ctaLabel}
              <ArrowRight className="ltr:ml-1 rtl:mr-1 rtl:rotate-180 h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
        <ul className="mt-7 space-y-2.5">
          {bullets.map((b) => (
            <li
              key={b}
              className="flex items-start gap-2.5 text-sm leading-relaxed text-neutral-600"
            >
              <span
                aria-hidden
                className="mt-2 h-1 w-1 shrink-0 rounded-full bg-neutral-400"
              />
              {b}
            </li>
          ))}
        </ul>

        {replaces && (
          <div className="mt-8 border-t border-neutral-200 pt-6">
            <p className="text-[10px] font-medium uppercase tracking-[0.16em] rtl:tracking-normal text-neutral-500">
              {replaces.label ?? 'Replaces'}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-neutral-700">
              {replaces.logos.map((logo) => logo.alt).join(' · ')}
            </p>
          </div>
        )}
      </div>

      {/* Right column — line items */}
      <div className="divide-y divide-neutral-200">
        {items.map((item) => (
          <div
            key={item.heading}
            className="grid gap-4 px-8 py-6 sm:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] sm:items-baseline sm:gap-8 md:px-10"
          >
            <div>
              <p className="text-sm font-medium text-neutral-900">
                {item.heading}
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-neutral-500">
                {item.description}
              </p>
            </div>
            <div className="sm:ltr:text-right sm:rtl:text-left">
              <p
                className={cn(
                  'font-sans tabular-nums text-neutral-900',
                  // Numeric prices get the display treatment in every locale.
                  // Matches "$0.75 – $0.90" as well as Arabic-Indic "٣ ⃁";
                  // word prices ("Free" / "مجاناً") stay at body size.
                  NUMERIC_PRICE.test(item.price)
                    ? 'text-2xl font-medium md:text-[28px]'
                    : 'text-base font-medium',
                )}
              >
                {item.price}
              </p>
              {item.detail && (
                <p className="mt-1 text-xs leading-relaxed text-neutral-500">
                  {item.detail}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  </article>
);

const Faqs = ({ c }: { c: Content }) => (
  <FaqSection
    id="faqs"
    eyebrow={c.faqsEyebrow}
    title={c.faqsTitle}
    subtitle={c.faqsSubtitle}
    items={c.faqs.map((item) => ({ question: item.q, answer: item.a }))}
  />
);
